/**
 * Runtime Environment Provider
 * 运行时环境变量提供者 - 纯客户端实现
 * 从 window.__ENV__ 或 import.meta.env 获取配置
 */

import { createContext, useContext, type ReactNode } from 'react'

interface EnvConfig {
  API_BASE_URL: string
  CDN_URL: string
  UPLOAD_API_BASE_URL: string
}

// 声明全局环境变量类型
declare global {
  interface Window {
    __ENV__?: {
      API_BASE_URL?: string
      CDN_URL?: string
      UPLOAD_API_BASE_URL?: string
    }
  }
}

// 获取环境变量配置
const getEnvConfig = (): EnvConfig => {
  const windowEnv = typeof window !== 'undefined' ? window.__ENV__ || {} : {}
  return {
    API_BASE_URL: windowEnv.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '',
    CDN_URL: windowEnv.CDN_URL || import.meta.env.VITE_CDN_URL || '',
    UPLOAD_API_BASE_URL: windowEnv.UPLOAD_API_BASE_URL || import.meta.env.VITE_UPLOAD_API_BASE_URL || '',
  }
}

const EnvContext = createContext<EnvConfig>(getEnvConfig())

export function EnvProvider({ children }: { children: ReactNode }) {
  const env = getEnvConfig()

  return <EnvContext.Provider value={env}>{children}</EnvContext.Provider>
}

/**
 * Hook to access runtime environment variables
 * 在任何组件中使用此 hook 访问环境变量
 */
export function useEnv() {
  const context = useContext(EnvContext)
  if (!context) {
    throw new Error('useEnv must be used within EnvProvider')
  }
  return context
}

// 导出配置获取函数供 API 客户端使用
export { getEnvConfig }
